import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GestoresService } from '../services/gestores.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-agenda-modal',
  templateUrl: './agenda-modal.component.html',
  providers: [MessageService]
})
export class AgendaModalComponent implements OnInit {
  @Input() gestor: any;
  
  displayDialog: boolean = false;
  form: FormGroup;
  loading: boolean = false;
  private destroy$ = new Subject<void>();

  // Arrays para los dropdowns
  agendas: any[] = [];
  
  // Datos de la tabla
  agendaItems: any[] = [];
  columns = [
    { field: 'descripcion', header: 'Descripción' },
    { field: 'prioridad', header: 'Prioridad' },
    { field: 'actions', header: 'Acciones' }
  ];

  // Estado de edición
  isEditing: boolean = false;
  editingItem: any = null;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private gestoresService: GestoresService
  ) {
    this.form = this.fb.group({
      agenda: [null, Validators.required],
      prioridad: [0, [Validators.required, Validators.min(0)]]
    });
  }

  ngOnInit(): void {
    this.loadAgendas();
    this.loadAgendaItems();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  open(): void {
    this.displayDialog = true;
  }

  close(): void {
    this.displayDialog = false;
    this.form.reset({ prioridad: 0 });
  }

  loadAgendas(): void {
    this.loading = true;
    this.gestoresService.getAgendas().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (agendas) => {
        this.agendas = agendas.map(agenda => ({
          descripcion: `${agenda.sValor} | ${agenda.sOpcion}`,
          valor: agenda.sValor
        }));
        this.loading = false;
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las agendas'
        });
        this.loading = false;
      }
    });
  }

  loadAgendaItems(): void {
    if (!this.gestor?.sAgencia) {
      this.agendaItems = [];
      return;
    }

    this.loading = true;
    this.gestoresService.getAgendaItems(this.gestor.sAgencia).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (items) => {
        this.agendaItems = items.map(item => ({
          id: item.iagenda,
          descripcion: `${item.iagenda} | ${item.sopcion}`,
          prioridad: item.iprioridad
        }));
        this.loading = false;
      },
      error: (error) => {
        console.error('Error al cargar items de agenda:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los items de agenda'
        });
        this.loading = false;
      }
    });
  }

  viewItem(item: any): void {
    // Cambiar a modo edición
    this.isEditing = true;
    this.editingItem = item;
    
    // Llenar el formulario con los datos del item
    this.form.patchValue({
      agenda: item.id.toString(),
      prioridad: item.prioridad
    });
    
    // Deshabilitar el campo agenda
    this.form.get('agenda')?.disable();
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.editingItem = null;
    this.form.reset({ prioridad: 0 });
    this.form.get('agenda')?.enable();
  }

  save(): void {
    if (this.form.valid) {
      this.loading = true;
      const formData = this.form.getRawValue();
      
      if (this.isEditing) {
        // Modo edición - actualizar agenda existente
        const agendaData = {
          sDeUsuario: this.gestor?.sAgencia || '',
          iAgenda: this.editingItem.id,
          iPrioridad: formData.prioridad
        };
        
        this.gestoresService.updateAgenda(agendaData).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: (response) => {
            if (response.success) {
              this.messageService.add({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Agenda actualizada correctamente'
              });
              this.loadAgendaItems();
              this.cancelEdit();
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al actualizar la agenda'
              });
            }
          },
          error: (error) => {
            console.error('Error al actualizar agenda:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al actualizar la agenda'
            });
          },
          complete: () => {
            this.loading = false;
          }
        });
      } else {
        // Modo creación - guardar nueva agenda
        const agendaData = {
          sDeUsuario: this.gestor?.sAgencia || '',
          iAgenda: parseInt(formData.agenda),
          iPrioridad: formData.prioridad
        };
        
        this.gestoresService.saveAgenda(agendaData).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: (response) => {
            if (response.success) {
              this.messageService.add({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Agenda guardada correctamente'
              });
              this.loadAgendaItems();
              this.form.reset({ prioridad: 0 });
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al guardar la agenda'
              });
            }
          },
          error: (error) => {
            console.error('Error al guardar agenda:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al guardar la agenda'
            });
          },
          complete: () => {
            this.loading = false;
          }
        });
      }
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
    }
  }

  deleteItem(item: any): void {
    // Confirmar eliminación con Swal
    Swal.fire({
      title: '¿Está seguro?',
      text: `¿Desea eliminar la agenda "${item.descripcion}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      iconColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.loading = true;
        
        const agendaData = {
          sDeUsuario: this.gestor?.sAgencia || '',
          iAgenda: item.id,
          iPrioridad: item.prioridad
        };
        
        this.gestoresService.deleteAgenda(agendaData).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: (response) => {
            if (response.success) {
              this.messageService.add({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Agenda eliminada correctamente'
              });
              this.loadAgendaItems(); // Recargar la tabla
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al eliminar la agenda'
              });
            }
          },
          error: (error) => {
            console.error('Error al eliminar agenda:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al eliminar la agenda'
            });
          },
          complete: () => {
            this.loading = false;
          }
        });
      }
    });
  }
} 